import React, { useState, useRef, useEffect, FC } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { Dayjs } from 'dayjs';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { alpha, styled } from '@mui/material/styles';
import { Typography } from '@mui/material';

// Extend PickersDayProps to include the "highlighted" prop
interface CustomPickersDayProps<TDate> extends PickersDayProps<TDate> {
  highlighted?: boolean;
  selector: 'start' | 'end' | 'none';
}

// Styled component for custom day rendering
const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== 'highlighted' && prop !== 'selector',
})<CustomPickersDayProps<Dayjs>>(({ theme, highlighted, selector }) => ({
  ...(selector === 'none' && {
    pointerEvents: 'none', // Disable all interactions
  }),
  ...(highlighted && {
    backgroundColor: alpha(theme.palette.primary.main, 0.3),
    color: theme.palette.primary.contrastText,
    borderRadius: '0px 0px 0px 0px',
  }),
  '&.start': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: '8px 0px 0px 8px',
  },
  '&.end': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: '0px 8px 8px 0px',
  },
  '&:hover': {
    backgroundColor: selector === 'none' ? 'inherit' : theme.palette.primary.dark,
    borderRadius: selector === 'none' ? 'none' : selector === 'end' ? '0px 8px 8px 0px' : '8px 0px 0px 8px',
    transition: 'all .3s ease-out',
  },
  transition: 'unset',
})) as React.ComponentType<CustomPickersDayProps<Dayjs>>;

interface DateRangePickerProps {
  fromDate: Dayjs | null;
  handleFromDateChange: (day: Dayjs | null) => void;

  toDate: Dayjs | null;
  handleToDateChange: (day: Dayjs | null) => void;
}

const DateRangePicker: FC<DateRangePickerProps> = ({ fromDate, handleFromDateChange, toDate, handleToDateChange }) => {
  const [open, setOpen] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([fromDate, toDate]);
  const [currentSelection, setCurrentSelection] = useState<'start' | 'end' | 'none'>('none');
  const [hoveredDate, setHoveredDate] = useState<Dayjs | null>(null);
  const calendarRef = useRef<HTMLDivElement | null>(null);

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    // Check if the click was outside the calendar and not on an element with the 'calendarOpener' class
    if (calendarRef.current && !calendarRef.current.contains(target) && !target.classList.contains('calendarOpener')) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleDateChange = (newDate: Dayjs | null) => {
    if (currentSelection === 'none') {
      return;
    }
    const newDates = currentSelection === 'start' ? [newDate, dateRange[1]] : [dateRange[0], newDate];

    setDateRange(newDates as [Dayjs | null, Dayjs | null]);

    if (currentSelection === 'start') {
      setCurrentSelection('end');
      handleFromDateChange(newDate);
    } else if (currentSelection === 'end') {
      setCurrentSelection('none');
      handleToDateChange(newDate);
      setOpen(false);
    } else {
      setCurrentSelection('none');
    }
  };

  const min = (a: Dayjs, b: Dayjs): Dayjs => (a.isBefore(b) ? a : b);
  const max = (a: Dayjs, b: Dayjs): Dayjs => (a.isBefore(b) ? b : a);

  const isHighlighted = (date: Dayjs): boolean => {
    if (currentSelection === 'none') {
      return !!(
        dateRange[0] &&
        dateRange[1] &&
        date.isAfter(dateRange[0], 'day') &&
        date.isBefore(dateRange[1], 'day')
      );
    }
    // Determine the range dynamically based on hoveredDate and selected dates
    const dateA = dateRange[0] || hoveredDate; // Prefer start date or hovered date
    const dateB = dateRange[1] || hoveredDate; // Prefer end date or hovered date

    // Ensure at least one valid range exists
    if (!dateA || !dateB) {
      return false;
    }

    // Calculate the range's start and end
    const start = min(dateA, dateB);
    const end = max(dateA, dateB);

    // Highlight the selected start or end dates or the dynamic range
    return (
      date.isAfter(start, 'day') && date.isBefore(end, 'day') // Dynamic range
    );
  };

  const renderDay = (day: Dayjs, _value: Dayjs | null, DayComponentProps: PickersDayProps<Dayjs>) => {
    const isStart = day.isSame(dateRange[0], 'day');
    const isEnd = day.isSame(dateRange[1], 'day');
    const highlighted = isHighlighted(day);

    const handleMouseEnter = () => {
      // Only update the hovered date if it's different from the current hovered date
      if (!hoveredDate || !hoveredDate.isSame(day, 'day')) {
        setHoveredDate(day);
      }
    };

    return (
      <CustomPickersDay
        {...DayComponentProps}
        className={`${isStart ? 'start' : ''} ${isEnd ? 'end' : ''}`}
        onMouseEnter={handleMouseEnter}
        highlighted={highlighted}
        selector={currentSelection}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ gap: 2, width: '100%', position: 'relative' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label="Start Date"
              value={dateRange[0]?.format('YYYY-MM-DD') || ''}
              onClick={() => {
                setOpen(true);
                setCurrentSelection('start');
                setDateRange((prev) => [null, prev[1]]);
              }}
              size="small"
              className="calenderOpener"
              focused={currentSelection === 'start'}
              InputProps={{
                className: 'calendarOpener',
              }}
              inputProps={{
                className: 'calendarOpener',
              }}
            />
            <Typography component="span">-</Typography>
            <TextField
              label="End Date"
              value={dateRange[1]?.format('YYYY-MM-DD') || ''}
              onClick={() => {
                setOpen(true);
                setCurrentSelection('end');
                setDateRange((prev) => [prev[0], null]);
              }}
              size="small"
              className="calenderOpener"
              focused={currentSelection === 'end'}
              InputProps={{
                className: 'calendarOpener',
              }}
              inputProps={{
                className: 'calendarOpener',
              }}
            />
          </Box>
        </Box>
        {open && (
          <div ref={calendarRef}>
            <StaticDatePicker
              key={currentSelection}
              displayStaticWrapperAs="desktop"
              value={currentSelection === 'none' ? null : currentSelection === 'start' ? dateRange[0] : dateRange[1]}
              onChange={handleDateChange}
              disableFuture
              minDate={currentSelection === 'end' && dateRange[0] ? dateRange[0] : undefined}
              maxDate={currentSelection === 'start' && dateRange[1] ? dateRange[1] : undefined}
              referenceDate={
                currentSelection === 'start'
                  ? dateRange[0] || hoveredDate || undefined
                  : dateRange[1] || hoveredDate || undefined
              } // Keep focus on the current selection or hovered date
              slots={{
                day: (props) => renderDay(props.day, null, props),
              }}
              sx={{ zIndex: 1, position: 'absolute', top: 40 }}
            />
          </div>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangePicker;
